import React, { useState } from "react";
import {
  Inbox, UserCheck, CheckCircle2, Lock, RotateCcw, Banknote, Ban, XCircle,
  History as HistoryIcon,
} from "lucide-react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { useLanguage } from "../../context/LanguageContext";

const statusBg = {
  draft: "bg-slate-50 border-slate-200",
  submitted: "bg-slate-50 border-slate-200",
  under_review: "bg-blue-50 border-blue-200",
  approved: "bg-emerald-50 border-emerald-200",
  paid: "bg-teal-50 border-teal-200",
  pending_re_edit: "bg-orange-50 border-orange-200",
  rejected: "bg-rose-50 border-rose-200",
  closed: "bg-slate-100 border-slate-200",
};

const statusIcon = {
  draft: Inbox,
  submitted: Inbox,
  under_review: UserCheck,
  approved: CheckCircle2,
  paid: Banknote,
  pending_re_edit: HistoryIcon,
  rejected: XCircle,
  closed: Lock,
};

export const LifecycleBanner = ({
  caseDoc,
  user,
  onClaim,
  onRelease,
  onApprove,
  onReject,
  onClose,
  onMarkPaid,
  onResubmitForEdit,
  busy,
}) => {
  const { t } = useLanguage();
  const status = caseDoc.status;
  const role = user?.role;
  const isOwner = role === "agent" && caseDoc.agent_id === user.id;
  const isAssignedReviewer = role === "reviewer" && caseDoc.reviewer_id === user.id;
  const isAdmin = role === "admin";

  const [approveOpen, setApproveOpen] = useState(false);
  const [approveMsg, setApproveMsg] = useState("");
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectMsg, setRejectMsg] = useState("");
  const [closeOpen, setCloseOpen] = useState(false);
  const [reEditOpen, setReEditOpen] = useState(false);
  const [reEditReason, setReEditReason] = useState("");
  // Phase 15 — two flavours of the same endpoint: "resubmit" (post-approval
  // re-edit, owner/admin) and "return" (admin/reviewer return-for-correction
  // from submitted/under_review). Only the modal copy differs.
  const [reEditVariant, setReEditVariant] = useState("resubmit");

  const Icon = statusIcon[status] || Inbox;

  // Determine the headline message
  let headline = "";
  let sub = "";
  switch (status) {
    case "draft":
      headline = t("case_status_draft");
      sub = t("case_lifecycle_draft");
      break;
    case "submitted":
      headline = t("case_status_in_pool");
      sub = role === "reviewer" || role === "admin"
        ? t("case_lifecycle_pool")
        : t("case_lifecycle_locked_post_submit");
      break;
    case "under_review":
      headline = t("case_status_under_review");
      sub = caseDoc.reviewer_name
        ? `${t("case_lifecycle_under_review")} ${caseDoc.reviewer_name}`
        : t("case_lifecycle_under_review");
      break;
    case "approved":
      headline = t("case_status_approved");
      sub = isOwner ? t("case_lifecycle_approved_agent") : t("case_lifecycle_approved");
      break;
    case "paid":
      headline = t("case_status_paid");
      sub = isAdmin ? t("case_lifecycle_paid_admin") : t("case_lifecycle_paid");
      break;
    case "rejected":
      headline = t("case_status_rejected");
      sub = t("case_lifecycle_rejected");
      break;
    case "pending_re_edit":
      headline = t("pending_re_edit_label");
      sub = isOwner
        ? t("pending_re_edit_agent_banner")
        : isAdmin
          ? t("pending_re_edit_admin_banner")
          : t("pending_re_edit_reviewer_banner");
      break;
    case "closed":
      headline = t("case_status_closed");
      sub = t("case_lifecycle_closed");
      break;
    default:
      headline = status;
  }

  // Action buttons
  const actions = [];
  if (status === "submitted") {
    if (role === "reviewer" && !caseDoc.reviewer_id) {
      actions.push(
        <Button
          key="claim"
          onClick={onClaim}
          disabled={busy}
          className="bg-[#0A2540] hover:bg-[#051626] text-white"
          data-testid="banner-claim-button"
        >
          {t("reviewer_action_claim")}
        </Button>
      );
    }
  }
  if (isAssignedReviewer && status === "under_review") {
    actions.push(
      <Button
        key="reject"
        variant="outline"
        onClick={() => setRejectOpen(true)}
        disabled={busy}
        className="border-rose-200 text-rose-700 hover:bg-rose-50"
        data-testid="banner-reject-button"
      >
        <XCircle className="h-4 w-4 mr-2" />
        {t("reviewer_action_reject_loan")}
      </Button>,
      <Button
        key="approve"
        onClick={() => setApproveOpen(true)}
        disabled={busy}
        className="bg-emerald-600 hover:bg-emerald-700 text-white"
        data-testid="banner-approve-button"
      >
        <CheckCircle2 className="h-4 w-4 mr-2" />
        {t("reviewer_action_approve_loan")}
      </Button>,
      <Button
        key="release"
        variant="ghost"
        onClick={onRelease}
        disabled={busy}
        className="text-slate-600 hover:bg-slate-100"
        data-testid="banner-release-button"
      >
        <RotateCcw className="h-4 w-4 mr-2" />
        {t("reviewer_action_release")}
      </Button>
    );
  }
  // Phase 12: agent marks paid once they've uploaded the customer payment slip
  if (isOwner && status === "approved") {
    const slipUploaded = !!(caseDoc.files || {}).customer_payment_slip;
    actions.push(
      <Button
        key="mark-paid"
        onClick={onMarkPaid}
        disabled={busy || !slipUploaded}
        className="bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-50"
        title={slipUploaded ? "" : t("payment_requires_slip")}
        data-testid="banner-mark-paid-button"
      >
        <Banknote className="h-4 w-4 mr-2" />
        {t("payment_mark_paid")}
      </Button>
    );
  }
  // Phase 12: admin closes only from `paid`
  if (isAdmin && status === "paid") {
    actions.push(
      <Button
        key="close"
        onClick={() => setCloseOpen(true)}
        disabled={busy}
        className="bg-[#0A2540] hover:bg-[#051626] text-white"
        data-testid="banner-close-button"
      >
        <Ban className="h-4 w-4 mr-2" />
        {t("payment_admin_close")}
      </Button>
    );
  }
  // Phase 14: re-submit-for-edit — owner agent OR admin, from approved/paid/closed
  if ((isOwner || isAdmin) && ["approved", "paid", "closed"].includes(status)) {
    actions.push(
      <Button
        key="resubmit-for-edit"
        variant="outline"
        onClick={() => { setReEditVariant("resubmit"); setReEditOpen(true); }}
        disabled={busy}
        className="border-orange-300 text-orange-700 hover:bg-orange-50"
        data-testid="banner-resubmit-for-edit-button"
      >
        <HistoryIcon className="h-4 w-4 mr-2" />
        {t("resubmit_for_edit")}
      </Button>
    );
  }
  // Phase 15: return-for-correction — admin (submitted+under_review) OR
  // assigned reviewer (under_review only).
  const canReturnForCorrection =
    (isAdmin && (status === "submitted" || status === "under_review")) ||
    (isAssignedReviewer && status === "under_review");
  if (canReturnForCorrection) {
    actions.push(
      <Button
        key="return-for-correction"
        variant="outline"
        onClick={() => { setReEditVariant("return"); setReEditOpen(true); }}
        disabled={busy}
        className="border-amber-400 text-amber-700 hover:bg-amber-50"
        data-testid="banner-return-for-correction-button"
      >
        <HistoryIcon className="h-4 w-4 mr-2" />
        {t("return_for_correction")}
      </Button>
    );
  }

  const onApproveSubmit = async () => {
    await onApprove(approveMsg.trim() || null);
    setApproveOpen(false);
    setApproveMsg("");
  };

  const onRejectSubmit = async () => {
    await onReject(rejectMsg.trim() || null);
    setRejectOpen(false);
    setRejectMsg("");
  };

  const onCloseSubmit = async () => {
    await onClose();
    setCloseOpen(false);
  };

  const onResubmitForEditSubmit = async () => {
    const reason = reEditReason.trim();
    if (reason.length < 5) return;
    await onResubmitForEdit(reason);
    setReEditOpen(false);
    setReEditReason("");
  };

  return (
    <>
      <div
        className={`border rounded-lg p-5 sm:p-6 flex flex-col md:flex-row md:items-center gap-4 ${statusBg[status] || statusBg.draft}`}
        data-testid="lifecycle-banner"
      >
        <div className="h-11 w-11 rounded-md bg-white/70 backdrop-blur flex items-center justify-center shrink-0">
          <Icon className="h-5 w-5 text-[#0A2540]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-display text-lg font-medium text-[#0A2540]">{headline}</p>
          <p className="text-sm text-slate-600 mt-0.5">{sub}</p>
          {caseDoc.reviewer_name && status !== "submitted" ? (
            <p className="text-xs text-slate-500 mt-1">
              {t("case_lifecycle_assigned_to")}: <span className="font-medium">{caseDoc.reviewer_name}</span>
            </p>
          ) : null}
        </div>
        {actions.length ? (
          <div className="flex flex-wrap gap-2 md:justify-end" data-testid="lifecycle-actions">
            {actions}
          </div>
        ) : null}
      </div>

      {/* Approve dialog */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent className="sm:max-w-md" data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle>{t("approve_dialog_title")}</DialogTitle>
            <DialogDescription>{t("approve_dialog_desc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="approve-msg">{t("approve_dialog_label")}</Label>
            <Textarea
              id="approve-msg"
              rows={3}
              value={approveMsg}
              onChange={(e) => setApproveMsg(e.target.value)}
              data-testid="approve-message-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveOpen(false)}>
              {t("modal_cancel")}
            </Button>
            <Button
              onClick={onApproveSubmit}
              disabled={busy}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="approve-submit-button"
            >
              {t("reviewer_action_approve_loan")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="sm:max-w-md" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>{t("reject_dialog_title")}</DialogTitle>
            <DialogDescription>{t("reject_dialog_desc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reject-msg">{t("reject_dialog_label")}</Label>
            <Textarea
              id="reject-msg"
              rows={3}
              value={rejectMsg}
              onChange={(e) => setRejectMsg(e.target.value)}
              placeholder={t("reject_dialog_placeholder")}
              data-testid="reject-message-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>
              {t("modal_cancel")}
            </Button>
            <Button
              onClick={onRejectSubmit}
              disabled={busy}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="reject-submit-button"
            >
              {t("reject_confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close confirmation dialog */}
      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent className="sm:max-w-md" data-testid="close-dialog">
          <DialogHeader>
            <DialogTitle>{t("close_dialog_title")}</DialogTitle>
            <DialogDescription>{t("close_dialog_desc")}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseOpen(false)}>
              {t("modal_cancel")}
            </Button>
            <Button
              onClick={onCloseSubmit}
              disabled={busy}
              className="bg-[#0A2540] hover:bg-[#051626] text-white"
              data-testid="close-submit-button"
            >
              {t("confirm_close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Phase 14: Re-submit for Edit confirmation dialog */}
      {/* Phase 14/15: Re-submit-for-Edit + Return-for-Correction share the same
          modal with role/source-status-specific copy. */}
      <Dialog open={reEditOpen} onOpenChange={setReEditOpen}>
        <DialogContent className="sm:max-w-md" data-testid="resubmit-for-edit-dialog">
          <DialogHeader>
            <DialogTitle>
              {reEditVariant === "return"
                ? t("return_for_correction_title")
                : t("resubmit_for_edit_title")}
            </DialogTitle>
            <DialogDescription>
              {reEditVariant === "return"
                ? t("return_for_correction_helper")
                : t("resubmit_for_edit_helper")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="re-edit-reason">
              {reEditVariant === "return"
                ? t("resubmit_for_edit_reason")
                : t("resubmit_for_edit_reason")} *
            </Label>
            <Textarea
              id="re-edit-reason"
              rows={3}
              value={reEditReason}
              onChange={(e) => setReEditReason(e.target.value)}
              placeholder={
                reEditVariant === "return"
                  ? t("return_for_correction_reason_placeholder")
                  : t("resubmit_for_edit_reason_placeholder")
              }
              data-testid="resubmit-for-edit-reason-input"
            />
            <p className="text-xs text-slate-500">
              {reEditReason.trim().length}/5
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReEditOpen(false)} disabled={busy}>
              {t("modal_cancel")}
            </Button>
            <Button
              onClick={onResubmitForEditSubmit}
              disabled={busy || reEditReason.trim().length < 5}
              className={
                reEditVariant === "return"
                  ? "bg-amber-600 hover:bg-amber-700 text-white"
                  : "bg-orange-600 hover:bg-orange-700 text-white"
              }
              data-testid="resubmit-for-edit-submit-button"
            >
              <HistoryIcon className="h-4 w-4 mr-2" />
              {reEditVariant === "return"
                ? t("return_for_correction")
                : t("resubmit_for_edit")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LifecycleBanner;
